import MovieClipSequence from "./MovieClipSequence";

/**
 * @class
 * @extends PIXI.Container
 * @memberOf P3
 */
export default class MovieClip extends PIXI.Sprite {
    /**
     * @param {MovieClipSequence} sequence
     * @param {string} [defaultAnimation=default]
     * @param {boolean} [autoUpdate=true]
     */
    constructor(sequence, defaultAnimation = "default", autoUpdate = true) {
        const texture = sequence.textures[defaultAnimation] ? sequence.textures[defaultAnimation][0] : PIXI.Texture.EMPTY;
        if (!texture) {
            throw new Error("No default texture found!");
        }
        super(texture);

        /**
         * @member {number}
         */
        this.animationSpeed = 24;

        /**
         * @member {boolean}
         */
        this.looping = false;

        /**
         * @member {object} signals
         * @member {signals.Signal} signals.animation
         * @member {signals.Signal} signals.animationComplete
         */
        this.signals = {
            animation: new signals.Signal(),
            animationComplete: new signals.Signal()
        };

        /**
         * @member {*}
         * @protected
         */
        this._frames = {};

        /**
         * @member {boolean}
         * @protected
         */
        this._playing = false;

        /**
         * @member {string}
         * @protected
         */
        this._defaultAnimation = defaultAnimation;

        /**
         * @member {number}
         * @protected
         */
        this._currentFrame = 0;

        /**
         * @member {number}
         * @protected
         */
        this._lastFrame = 0;

        /**
         * @member {string}
         * @protected
         */
        this._currentAnimationName = this._defaultAnimation;

        /**
         * @type {boolean}
         * @protected
         */
        this._autoUpdate = false;

        for (let i in sequence.textures) {
            if (sequence.textures.hasOwnProperty(i)) {
                this._frames[i] = [];
                for (let j = 0; j < sequence.textures[i].length; ++ j) {
                    this._frames[i].push({
                        texture: sequence.textures[i][j],
                        callback: null,
                        scope: null
                    });
                }
            }
        }
        this.autoUpdate = autoUpdate;
    }

    /**
     * @param {object} options
     * @param {boolean} [options.children=false]
     * @param {boolean} [options.texture=false]
     * @param {boolean} [options.baseTexture=false]
     */
    destroy(options) {
        this.signals.animation.dispose();
        this.signals.animationComplete.dispose();

        this.autoUpdate = false;

        super.destroy(options);
    }

    /**
     * @param {boolean} [looping]
     */
    play(looping) {
        if (typeof looping === "boolean") {
            this.looping = looping;
        }
        this._playing = true;
    }

    stop() {
        this._playing = false;
    }

    /**
     * @param {number|string} frame
     * @param {boolean} looping
     */
    gotoAndPlay(frame, looping) {
        if (typeof frame !== "string") {
            this._currentFrame = this._lastFrame = frame;
        } else if (this._frames[frame]) {
            this._currentFrame = this._lastFrame = 0;
            this._currentAnimationName = frame;
        }
        if (typeof looping === "boolean") {
            this.looping = looping;
        }
        this._playing = true;
    }

    /**
     * @param {number|string} frame
     */
    gotoAndStop(frame) {
        if (typeof frame !== "string") {
            this._currentFrame = frame;
        } else if (this._frames[frame]) {
            this._currentFrame = 0;
            this._currentAnimationName = frame;
        }
        this._playing = false;

        const frames = this._frames[this._currentAnimationName];
        const round = (this._currentFrame + 0.5) | 0;
        this.texture = frames[round % frames.length].texture;
    }

    /**
     * @param {number} frame
     * @param {function} callback
     * @param {*} [scope]
     * @param {string} [name=default]
     */
    addFrameScript(frame, callback, scope, name = "default") {
        scope = scope || window;
        if (this._frames[name]) {
            this._frames[name][frame].callback = callback;
            this._frames[name][frame].scope = callback ? (scope | window) : null;
        }
    }

    /**
     * @param {number} dt
     */
    update(dt) {
        if (!this._playing || !this.getTotalFrames()) return;

        this._autoUpdate && (dt = dt / PIXI.settings.TARGET_FPMS / 1000);
        this._currentFrame += this.animationSpeed * dt;

        const frames = this._frames[this._currentAnimationName];
        const round = (this._currentFrame + 0.5) | 0;
        const index = round % (frames.length + 1);

        this._currentFrame = this._currentFrame % frames.length;
        if (index > 0 && index !== this._lastFrame) {
            this._lastFrame = index;
            (frames[index - 1].callback) && frames[index - 1].callback.call(frames[index - 1].scope);
        }

        if (this.looping || round < frames.length) {
            this.texture = frames[round % frames.length].texture;
            if (round >= frames.length - 1) {
                this.signals.animation.dispatch(this, this._currentAnimationName);
            }
        } else if (round >= frames.length - 1) {
            this.gotoAndStop(frames.length - 1);
            this.signals.animationComplete.dispatch(this, this._currentAnimationName);
        }
    }

    /**
     * @param {string} [name]
     * @returns {number}
     */
    getTotalFrames(name) {
        name = name || this._defaultAnimation;
        const frames = this._frames[name];
        return frames ? frames.length : 0;
    }

    /**
     * @returns {boolean}
     */
    get playing() {
        return this._playing;
    }

    /**
     * @returns {number}
     */
    get currentFrame() {
        return this._currentFrame;
    }

    /**
     * @returns {string}
     */
    get currentAnimationFrame() {
        return this._currentAnimationName;
    }

    /**
     * @returns {number}
     */
    get totalFrames() {
        return this.getTotalFrames();
    }

    /**
     * @returns {boolean}
     */
    get autoUpdate() {
        return this._autoUpdate;
    }

    /**
     * @param {boolean} value
     */
    set autoUpdate(value) {
        const ticker = PIXI.ticker.shared;
        if (this._autoUpdate && !value) {
            ticker.remove(this.update, this);
        } else if (!this._autoUpdate && value) {
            ticker.add(this.update, this);
        }
        this._autoUpdate = value;
    }
}